import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../auth/AuthProvider';
import type { ToolProps } from '../olmps/ToolHost';
import { NemosNotes } from './nemos/notes';
import { NemosFiles } from './nemos/files';
import { NemosRecords } from './nemos/records';
import { NemosRecall } from './nemos/recall';
import { Atmos } from '../v10/ui';
import { ScreenHelp } from '../v10/ScreenHelp';
import { nemosHelp } from '../olmps/help/nemosHelp';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';

// ---------------------------------------------------------------------------
// NEMOS — native replica of the web hub at src/app/nemos/ (page.tsx +
// notes/files/records/recall page.tsx, ~2270 lines). NEMOS is the
// Notes / Files / Records / Recall hub: same Supabase tables as web
// (notes, note_folders, hrmez_local_folders, hrmez_local_files,
// hrmez_decisions, hrmez_ambiguous, hrmez_reports, recalls, sounds), same
// CRUD, same folder tree navigation, same recall list/edit flow. Web's
// top nav bar (src/app/nemos/layout.tsx) becomes a segmented section
// switcher here, matching mobile's tap-navigation convention.
//
// Per-section fidelity notes (see each file in ./nemos/ for full detail):
//  - Notes: web's tiptap rich-text editor is not ported (no rich-text dep
//    on mobile); this edits the SAME `content` HTML column as plain text.
//  - Files: Google Drive mode is web-only (server OAuth flow); local drive
//    is 1:1. Multi-file upload of arbitrary types is web-only; mobile
//    uploads images via expo-image-picker.
//  - Records: Move Log / Review / Run History tabs are 1:1 (Supabase
//    direct). Index tab calls the same /api/hrmez/drive server route as
//    web and will correctly report "connect Google Drive first" when no
//    Drive token exists (the common case).
//  - Recall: full CRUD/list/preview is 1:1 against the `recalls` table.
//    Preset sounds (served from web's public/sounds directory) are
//    web-only; custom-uploaded sounds (`sounds` table) work identically.
//    Scheduled OS notifications are out of scope — the web page itself
//    doesn't schedule OS notifications either, it just persists recall
//    rows for its own in-app alert timer.
// ---------------------------------------------------------------------------

type Section = 'notes' | 'files' | 'records' | 'recall';

const SECTIONS: { key: Section; label: string }[] = [
  { key: 'notes', label: 'Notes' },
  { key: 'files', label: 'Files' },
  { key: 'records', label: 'Records' },
  { key: 'recall', label: 'Recall' },
];

export function NemosTool(_props: ToolProps) {
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [section, setSection] = useState<Section>('notes');

  if (!userId) {
    return (
      <Atmos>
        <View style={styles.center}>
          <Text style={styles.mutedText}>Please log in to use NEMOS.</Text>
        </View>
      </Atmos>
    );
  }

  return (
    <Atmos>
    <View style={styles.root}>
      <View style={styles.titleRow}>
        <Text style={styles.title}>N.E.M.O.S</Text>
        <ScreenHelp help={nemosHelp} />
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.navScroll}>
        <View style={styles.navBar}>
          {SECTIONS.map((s) => (
            <Pressable key={s.key} style={[styles.navBtn, section === s.key && styles.navBtnActive]} onPress={() => setSection(s.key)}>
              <Text style={[styles.navBtnText, section === s.key && styles.navBtnTextActive]}>{s.label}</Text>
            </Pressable>
          ))}
        </View>
      </ScrollView>

      <View style={styles.body}>
        {section === 'notes' && <NemosNotes userId={userId} />}
        {section === 'files' && <NemosFiles userId={userId} />}
        {section === 'records' && <NemosRecords userId={userId} />}
        {section === 'recall' && <NemosRecall userId={userId} />}
      </View>
    </View>
    </Atmos>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1, padding: 14, gap: 10 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  mutedText: { fontSize: 14, color: c.slate },
  titleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 22, fontWeight: '900', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  navScroll: { flexGrow: 0 },
  navBar: {
    flexDirection: 'row',
    gap: 6,
    backgroundColor: c.glass,
    borderRadius: radii.pill,
    padding: 4,
    ...neu.inset,
  },
  navBtn: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: radii.pill },
  navBtnActive: { backgroundColor: c.primary, ...neu.glowPrimary },
  navBtnText: { fontSize: 13, fontWeight: '800', color: c.slate },
  navBtnTextActive: { color: t.onGold },
  body: { flex: 1 },
}),
);
